import type { ParentComponent } from "solid-js";
import { A, useNavigate, useLocation } from "@solidjs/router";
import { Show, createEffect, createSignal } from "solid-js";
import { useAppState } from "@core/state/AppState";
import { ToastHost } from "@ui/components/ToastHost";
import { InstallPrompt } from "@ui/components/InstallPrompt";

export const ProtectedLayout: ParentComponent = (props) => {
  const appState = useAppState();
  const state = appState.snapshot();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = createSignal(false);

  createEffect(() => {
    if (state.authStatus() === "signed-out") {
      navigate("/login", { replace: true });
    }
  });

  const showLoading = () => {
    const status = state.authStatus();
    return status === "loading" || status === "verifying";
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  return (
    <Show
      when={!showLoading()}
      fallback={
        <div class="auth-loading-screen">
          <div class="auth-loading-backdrop"></div>
          <div class="auth-loading-content">
            <div class="auth-loading-logo">
              <img src="/logo.png" alt="HappyTidy" />
            </div>
            <h1 class="auth-loading-title">Loading HappyTidy...</h1>
            <p class="auth-loading-subtitle">Getting everything ready for you</p>
          </div>
        </div>
      }
    >
      <div class="app-container text-body">
      {/* Mobile menu button */}
      <button
        class="mobile-menu-btn"
        onClick={() => setSidebarOpen(!sidebarOpen())}
        aria-label="Toggle menu"
      >
        <i class="fa-solid fa-bars"></i>
      </button>

      {/* Sidebar overlay for mobile */}
      <Show when={sidebarOpen()}>
        <div class="sidebar-overlay" onClick={closeSidebar}></div>
      </Show>

      {/* Sidebar */}
      <aside classList={{ sidebar: true, "sidebar--open": sidebarOpen() }}>
        <div class="sidebar-header">
          <img src="/logo.png" alt="HappyTidy" class="sidebar-logo" />
          <div class="sidebar-brand">
            <h2 class="sidebar-brand-name">HappyTidy</h2>
            <p class="sidebar-brand-motto">From mess to happiness</p>
          </div>
        </div>
        <nav class="sidebar-nav">
          <A
            href="/"
            classList={{ "sidebar-link": true, active: location.pathname === "/" }}
            onClick={closeSidebar}
          >
            <i class="fa-solid fa-comments"></i>
            <span>Chat</span>
          </A>
          <A
            href="/settings"
            classList={{ "sidebar-link": true, active: location.pathname.startsWith("/settings") }}
            onClick={closeSidebar}
          >
            <i class="fa-solid fa-gear"></i>
            <span>Settings</span>
          </A>
        </nav>

        <div class="sidebar-footer">
          <button
            class="sidebar-logout"
            onClick={() => {
              appState.signOut();
              closeSidebar();
            }}
          >
            <i class="fa-solid fa-right-from-bracket"></i>
            <span>Sign Out</span>
          </button>

          <div class="sidebar-legal">
            <div class="sidebar-legal-links">
              <a href="https://hpptd.com/terms.html" target="_blank" rel="noopener noreferrer">Terms of Use</a>
              <span class="sidebar-legal-divider">•</span>
              <a href="https://hpptd.com/privacy.html" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
            <p class="sidebar-copyright">© Happy Inc</p>
            <p class="sidebar-version">v{__APP_VERSION__} • {__BUILD_TIME__}</p>
          </div>
        </div>
      </aside>

      <Show when={state.isOffline()}>
        <div class="offline-banner">You are offline. Messages will send when you're connected.</div>
      </Show>

      {/* Header - OUTSIDE app-container, INSIDE main - exactly like template */}
      <main class="app-main">
        <header class="app-header">
          <div class="logo-small">
            <img src="/logo.png" alt="HappyTidy" />
            <h5>HappyTidy</h5>
          </div>
        </header>

        <Show when={!showLoading()} fallback={<div class="loading-state">Loading…</div>}>
          {props.children}
        </Show>
      </main>

      <ToastHost />
      <InstallPrompt />
      </div>
    </Show>
  );
};
